/**
 * ParseDriver — the runtime-agnostic per-page parsing contract.
 *
 * A driver turns a base64-encoded PDF into the document IR, one page at a time.
 * The okra document runtime (`@okrapdf/document-runtime`) injects a driver so the
 * SAME durable orchestrator can run against an in-worker VLM (BYOK), a stateless
 * parser sidecar over HTTP, or any registered backend — without branching on
 * tenancy. Cloud and self-deploy differ only in which driver `env` resolves.
 *
 * Block/bbox shapes REUSE the canonical IR in `./types` — drivers must normalize
 * coordinates to {@link CanonicalBbox} (0–1 relative). Do not invent a new block type.
 */
import { Effect, Schema } from 'effect';

import type { CanonicalBbox } from './types';

/**
 * One parsed layout element. Structurally compatible with the self-host
 * `Block` and the canonical block, but pinned to {@link CanonicalBbox} so every
 * driver emits the same 0–1 coordinate space the hydrator/citation layer expects.
 */
export interface ParsedBlock {
  /** Raw parser-specific type, preserved for fidelity (resolve at the render/export boundary). */
  type: string;
  /** Display/category label (e.g. 'Table', 'Text', 'Title'). */
  label?: string;
  /** Text / value content of the block. */
  value?: string;
  /** Normalized 0–1 bounding box. */
  bbox?: CanonicalBbox;
  /** Driver-reported confidence, 0–1, when available. */
  confidence?: number;
}

/**
 * Per-page parse driver. The runtime calls `countPages` once to size the run,
 * then `parsePage` for each page 1..N (resumable, at-least-once per page).
 */
export interface ParseDriver {
  /** Stable driver id (e.g. 'gemini-vision', 'liteparse'). Recorded on the run. */
  id: string;
  /** Total page count of the document. */
  countPages(pdfBase64: string): Promise<number>;
  /**
   * Parse a single 1-indexed page into blocks.
   * @param ctx.modelKey optional per-run model/credential hint (e.g. BYOK API key or variant).
   */
  parsePage(
    pdfBase64: string,
    page: number,
    ctx: { modelKey?: string },
  ): Promise<{ blocks: ParsedBlock[] }>;
}

export type ParseDriverOperation = 'count-pages' | 'parse-page';

/** Typed failure used when a legacy Promise-only driver is lifted into Effect. */
export class ParseDriverExecutionError extends Schema.TaggedError<ParseDriverExecutionError>()(
  'ParseDriverExecutionError',
  {
    driverId: Schema.String,
    operation: Schema.Literals(['count-pages', 'parse-page']),
    cause: Schema.Defect(),
  },
) {}

/**
 * Effect-native per-page driver boundary. Stable Promise methods are derived
 * from these programs by {@link defineEffectParseDriver}.
 */
export interface EffectParseDriver<E = ParseDriverExecutionError> {
  readonly id: string;
  countPagesEffect(pdfBase64: string): Effect.Effect<number, E>;
  parsePageEffect(
    pdfBase64: string,
    page: number,
    ctx: { modelKey?: string },
  ): Effect.Effect<{ blocks: ParsedBlock[] }, E>;
}

export type DefinedEffectParseDriver<E = ParseDriverExecutionError> = ParseDriver &
  EffectParseDriver<E>;

export interface DefineEffectParseDriverOptions<E> {
  /** Restore an established external error at the Promise compatibility edge. */
  readonly restoreError?: (error: E) => unknown;
}

async function runDriverEffect<A, E>(
  program: Effect.Effect<A, E>,
  restoreError?: (error: E) => unknown,
): Promise<A> {
  try {
    return await Effect.runPromise(program);
  } catch (error) {
    throw restoreError ? restoreError(error as E) : error;
  }
}

/** Author one native implementation while retaining the published Promise API. */
export function defineEffectParseDriver<E>(
  driver: EffectParseDriver<E>,
  options: DefineEffectParseDriverOptions<E> = {},
): DefinedEffectParseDriver<E> {
  return {
    ...driver,
    countPages: (pdfBase64) =>
      runDriverEffect(driver.countPagesEffect(pdfBase64), options.restoreError),
    parsePage: (pdfBase64, page, ctx) =>
      runDriverEffect(driver.parsePageEffect(pdfBase64, page, ctx), options.restoreError),
  };
}

export function isEffectParseDriver<E = unknown>(
  driver: ParseDriver | EffectParseDriver<E>,
): driver is EffectParseDriver<E> {
  const candidate = driver as Partial<EffectParseDriver<E>>;
  return (
    typeof candidate.countPagesEffect === 'function' &&
    typeof candidate.parsePageEffect === 'function'
  );
}

/** Recover the original Promise rejection from the legacy-driver lift. */
export function parseDriverFailureCause(error: unknown): unknown {
  return error instanceof ParseDriverExecutionError ? error.cause : error;
}

const liftedPromiseDrivers = new WeakMap<
  ParseDriver,
  EffectParseDriver<ParseDriverExecutionError>
>();

/**
 * Normalize any published driver to the native contract. Existing native
 * programs pass through unchanged; Promise-only custom drivers are lifted once.
 */
export function effectParseDriverFromPromise(driver: ParseDriver): EffectParseDriver<unknown> {
  if (isEffectParseDriver(driver)) return driver;
  const cached = liftedPromiseDrivers.get(driver);
  if (cached) return cached;
  const lifted: EffectParseDriver<ParseDriverExecutionError> = {
    id: driver.id,
    countPagesEffect: (pdfBase64) =>
      Effect.tryPromise({
        try: () => driver.countPages(pdfBase64),
        catch: (cause) =>
          new ParseDriverExecutionError({
            driverId: driver.id,
            operation: 'count-pages',
            cause,
          }),
      }),
    parsePageEffect: (pdfBase64, page, ctx) =>
      Effect.tryPromise({
        try: () => driver.parsePage(pdfBase64, page, ctx),
        catch: (cause) =>
          new ParseDriverExecutionError({
            driverId: driver.id,
            operation: 'parse-page',
            cause,
          }),
      }),
  };
  liftedPromiseDrivers.set(driver, lifted);
  return lifted;
}
